import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import DatePicker from 'components/final-form/DatePicker';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import TextArea from 'components/final-form/TextArea';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import LoaderComponent from 'components/LoaderComponent';
import ReportDiagnosisComponent from 'components/patient/upsert/ReportDiagnosisComponent';
import PatientReportPdfModal from 'components/pdf-modal/PatientReportPdfModal';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Icon, Message, Modal } from 'semantic-ui-react';
import {
  generateDoctorReport,
  getLabResultData,
  getMedicamentData,
  getSynopsisReportData
} from 'services/doctorReportServices';
import { getPdfDocumentUrl } from 'services/patientRecordServices';
import styled from 'styled-components';
import { PatientDocumentType } from 'ts-types/api.enums';
import {
  DoctorReportDto,
  DoctorReportLabResultDataDto,
  DoctorReportMedicamentDataDto,
  DoctorReportSynopsisDataDto,
  PatientDto,
  PdfResponse,
  ReportDiagnosisDto
} from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';

const ModalHeaderDiv = styled.div`
  & .person-icon {
    padding: 0 1.1rem 0 0.75rem;
  }
`;

const addButton = {
  marginBottom: 10,
  marginTop: 10
};

const StyledBorderGridRow = styled(Grid.Row)`
     border-bottom: 1px solid rgba(34,36,38,.25);
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  modalOpen: boolean;
  onCloseModal: (savedDocument: boolean) => void;
  patient: PatientDto;
}

interface DoctorReportExtended extends DoctorReportDto {
  diagnosis:  ReportDiagnosisDto[]
}

const UpsertDoctorReportModal = (props: Props) => {

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  const {
    modalOpen,
    onCloseModal, patient,
    getMessage,
    options
  } = props;

  const [diagnoses, setDiagnoses] =
      useState([] as ReportDiagnosisDto[]);
  const [addDiagnose, setAddDiagnose] =
      useState(false);
  const [addDiagnoseIx, setAddDiagnoseIx] =
      useState(undefined as number | undefined);
  const [selectedDiagnoses, setSelectedDiagnoses] =
      useState([] as ReportDiagnosisDto[]);
  const [doctorReportPdfResponse, setDoctorReportPdfResponse] =
      useState(undefined as PdfResponse | undefined);
  const [doctorReportPdfModalOpen, setDoctorReportPdfModalOpen] =
      useState(false);
  const [email, setEmail] = useState("" as string);
  const [reportDto, setReportDto] =
      useState(undefined as DoctorReportDto | undefined);

  const yesNoEnum = {
    YES: "Yes",
    NO: "No"
  };

  const yesNoValues: Array<DropdownOption> = Object.values(yesNoEnum).map((value: string) => {
    return {
      key: value,
      text: getMessage(`button.${value.toLowerCase()}`),
      value: value
    }
  });

  const [yesNoOptions] = useState(yesNoValues as DropdownOption[]);
  const [errorMessage, setErrorMessage] = useState(undefined as string | undefined);
  const [successfullySavedDocument, setSuccessfullySavedDocument] = useState(undefined as string | undefined);
  const [successfullySendEmail, setSuccessfullySendEmail] = useState(undefined as string | undefined);
  const [requiredDataLoaded, setRequiredDataLoaded] = useState(false);

  const initialValues = useRef<Partial<DoctorReportExtended>>({});

  useEffect(() => {

    initialValues.current = {
      reportName: "Doctor report",
      specialCircumstances: "No",
      allocateAppropriateWork: "No",
      permanentDisadvantage: "No",
    }

    const fetchData = async () => {
      try {

        Promise.all([
              getSynopsisReportData(patient.id, cancelTokenSource),
              getMedicamentData(patient.id, cancelTokenSource),
              getLabResultData(patient.id, cancelTokenSource)
            ])
            .then(responses => {
              const synopsisData: DoctorReportSynopsisDataDto = responses[0];
              const medicamentData: DoctorReportMedicamentDataDto = responses[1];
              const labResultData: DoctorReportLabResultDataDto = responses[2];

              initialValues.current = {
                ...initialValues.current,
                synopsisData: synopsisData,
                diagnosis: synopsisData.diagnosis,
                medicamentData: medicamentData,
                labResultData: labResultData
              }
              setDiagnoses(synopsisData.diagnosis!);
            })
            .catch((e: any) => handleError(e.response.data));

      } catch (e) {
        handleError(e);
      } finally {
        setRequiredDataLoaded(true);
      }
    };

    fetchData();
  }, []);

  const openDoctorReport = async (values: Partial<DoctorReportDto>) => {

    const patientId = patient.id;

    const filteredDiagnoses = diagnoses.filter(d => d.selected);

    const request: Partial<DoctorReportDto>  = {
      ...values,
      synopsisData:{
        diagnosis: filteredDiagnoses
      },
      treaterId: options.currentTreater.id,
      patientId: patientId
    }

    const reportDto: DoctorReportDto  = {
      ...request
    }

    setReportDto(reportDto);
    setEmail(values.email != undefined ? values.email : "");

    generateDoctorReport(patientId, request, cancelTokenSource)
        .then(response => {
          openDoctorReportPdf(response);
        })
        .catch((e: any) => handleError(e.response.data));
  }

  const openDoctorReportPdf = (reportResponse: PdfResponse) => {
    setDoctorReportPdfResponse(reportResponse);
    setDoctorReportPdfModalOpen(true);
  }

  const closeDoctorReportPdf = () => {
    setDoctorReportPdfModalOpen(false);
    setDoctorReportPdfResponse(undefined);
  }

  const getDoctorReportPdfUrl = (pdfResponse: PdfResponse) => {
    return getPdfDocumentUrl(pdfResponse.pdfId, pdfResponse.pdfName);
  }

  const saveDocument = (successfullySaved: boolean) => {

    if (successfullySaved) {
      setSuccessfullySavedDocument(getMessage("synopsisReport.successfullySavedReport"));
    }

    closeDoctorReportPdf();
    onCloseModal(true);
  }

  const sendEmail = (successfullySendEmail: boolean) => {

    if (successfullySendEmail) {
      setSuccessfullySendEmail(getMessage("synopsisReport.successfullySendEmail"));
    }

    closeDoctorReportPdf();
    onCloseModal(true);
  }

  const handleError = (error: any) => {
    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [
      errorUtils.unknownPatient
    ];
    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  };

  const addNewDiagnose = (fields: any, index: number) => {

    const newDiagnose: Partial<ReportDiagnosisDto> = {
      treatmentDate: moment().format("YYYY-MM-DD"),
      code: "",
      description: "",
      editable: true,
      hidden: false
    }

    fields.push(newDiagnose);

    setAddDiagnose(true);
    setAddDiagnoseIx(index);
  };

  const changeDiagnoses = (diagnoses: ReportDiagnosisDto[]) => {
    setDiagnoses(diagnoses);
  }

  const changeAddEditDiagnose = (addDiagnose: boolean) => {
    setAddDiagnose(addDiagnose);
  }

  const changeAddEditDiagnoseIx = (index?: number) => {
    setAddDiagnoseIx(index);
  }

  const changeSelectedDiagnoses = (diagnoses: ReportDiagnosisDto[]) => {
    setSelectedDiagnoses(diagnoses);
  }

  const renderModalHeader = () => {
    return <>
      <Icon name="user circle"/> {" " + patient.name + " " + patient.firstName + " "}
    </>
  }

  const renderDiagnosesSegment = (form: FormApi): JSX.Element => {

    return (
        <ReportDiagnosisComponent
            diagnoses={diagnoses}
            addDiagnose={addDiagnose}
            addDiagnoseIx={addDiagnoseIx}
            changeDiagnoses={changeDiagnoses}
            changeSelectedDiagnoses={changeSelectedDiagnoses}
            changeAddEditDiagnose={changeAddEditDiagnose}
            changeAddEditDiagnoseIx={changeAddEditDiagnoseIx}
            formChange={form.change}
        />
    );
  }

  const renderForm = ({form, values, submitting, handleSubmit}: FormRenderProps): JSX.Element => {

    return (
        <React.Fragment>
          <form onSubmit={handleSubmit}>
            {
                errorMessage
                && (
                    <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                      {errorMessage}
                    </StyledErrorMessage>
                )
            }
            {
                successfullySavedDocument
                && (
                    <Message success onDismiss={() => setSuccessfullySavedDocument(undefined)}>
                      {successfullySavedDocument}
                    </Message>
                )

            }
            {
                successfullySendEmail
                && (
                    <Message success onDismiss={() => setSuccessfullySendEmail(undefined)}>
                      {successfullySendEmail}
                    </Message>
                )

            }
            <FormContainer>
              <FormContent>
                <PraxFormGrid>
                  <Grid.Row>
                    <Grid.Column width={2} style={{fontWeight: "bold"}}>
                      {getMessage("interventionOrderItem.reportNamePrefix")}:
                    </Grid.Column>
                    <Grid.Column width={6} style={{fontWeight: "bold"}}>
                      <Field
                          name="reportName"
                          component={Input}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2} style={{fontWeight: "bold"}}>
                      {getMessage("doctorReport.modal.sendTo")}
                    </Grid.Column>
                    <Grid.Column width={6}>
                      <Field
                          name="email"
                          component={Input}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2} style={{fontWeight: "bold"}}>
                      {getMessage("doctorReport.modal.damageNumber")}
                    </Grid.Column>
                    <Grid.Column width={3} style={{fontWeight: "bold"}}>
                      <Field
                          name="damageNumber"
                          component={Input}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2} style={{fontWeight: "bold"}}>
                      {getMessage("doctorReport.modal.damageDate")}
                    </Grid.Column>
                    <Grid.Column width={3}>
                      <Field
                          name="damageDate"
                          component={DatePicker}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2} style={{fontWeight: "bold"}}>
                      {getMessage("doctorReport.modal.employer")}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}>
                      {getMessage("name")}
                    </Grid.Column>
                    <Grid.Column width={6}>
                      <Field
                          name="employerName"
                          component={Input}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}>
                      {getMessage("address")}, {getMessage("zip")} {getMessage("city")}:
                    </Grid.Column>
                    <Grid.Column width={6}>
                      <Field
                          name="employerAddress"
                          component={Input}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={8} style={{fontWeight: "bold"}}>
                      {getMessage("doctorReport.modal.diagnosis")}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <div style={{width: "100%"}}>
                        {renderDiagnosesSegment(form)}
                      </div>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <FieldArray name="diagnosis">
                        {({fields}) => (
                            <Button
                                type="button"
                                compact
                                style={addButton}
                                onClick={() => addNewDiagnose(fields, fields.length!)}
                            >
                              {getMessage('button.add')}
                            </Button>
                        )}
                      </FieldArray>
                    </Grid.Column>
                  </Grid.Row>
                  <StyledBorderGridRow/>
                  <Grid.Row>
                    <Grid.Column width={2} style={{fontWeight: "bold"}}>
                      {getMessage("doctorReport.modal.history")}
                    </Grid.Column>
                    <Grid.Column width={14}>
                      {getMessage("doctorReport.modal.actualCondition")}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={14}>
                      <Field
                          name="synopsisData.currentCondition"
                          component={TextArea}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={14}>
                      {getMessage("doctorReport.modal.prognosis")}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={14}>
                      <Field
                          name="prognosis"
                          component={TextArea}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={12}>
                      {getMessage("doctorReport.modal.specialCircumstances")}
                    </Grid.Column>
                    <Grid.Column width={2}>
                      <Field
                          name="specialCircumstances"
                          component={Select}
                          options={yesNoOptions}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={3}>
                      {getMessage("doctorReport.modal.whenYes")}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={14}>
                      <Field
                          name="specialCircumstancesTrue"
                          component={TextArea}
                          fluid
                          disabled={values.specialCircumstances === "No"}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <StyledBorderGridRow/>
                  <Grid.Row>
                    <Grid.Column width={2} style={{fontWeight: "bold"}}>
                      {getMessage("doctorReport.modal.therapy")}
                    </Grid.Column>
                    <Grid.Column width={14}>
                      {getMessage("doctorReport.modal.currentTreatment")}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={14}>
                      <Field
                          name="currentTreatment"
                          component={TextArea}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={14}>
                      {getMessage("doctorReport.modal.procedure")}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={14}>
                      <Field
                          name="synopsisData.procedure"
                          component={TextArea}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={14}>
                      {getMessage("doctorReport.modal.consultationIntervals")}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={14}>
                      <Field
                          name="consultationIntervals"
                          component={TextArea}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={14}>
                      {getMessage("doctorReport.modal.treatmentDuration")}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={14}>
                      <Field
                          name="treatmentDuration"
                          component={TextArea}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <StyledBorderGridRow/>
                  <Grid.Row>
                    <Grid.Column width={2} style={{fontWeight: "bold"}}>
                      {getMessage("doctorReport.modal.medicament")}
                    </Grid.Column>
                    <Grid.Column width={14}>
                      <Field
                          name="medicamentData.medicament"
                          component={TextArea}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <StyledBorderGridRow/>
                  <Grid.Row>
                    <Grid.Column width={2} style={{fontWeight: "bold"}}>
                      {getMessage("doctorReport.modal.labResult")}
                    </Grid.Column>
                    <Grid.Column width={14}>
                      <Field
                          name="labResultData.labResult"
                          component={TextArea}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <StyledBorderGridRow/>
                  <Grid.Row>
                    <Grid.Column width={2} style={{fontWeight: "bold"}}>
                      {getMessage("doctorReport.modal.startingWork")}
                    </Grid.Column>
                    <Grid.Column width={4}>
                      {getMessage("doctorReport.modal.startingWork.resumption1")}
                    </Grid.Column>
                    <Grid.Column width={2}>
                      <Field
                          name="resumption1"
                          component={Input}
                          fluid
                      />
                    </Grid.Column>
                    <Grid.Column width={3}>
                      {getMessage("doctorReport.modal.startingWork.resumption2")}
                    </Grid.Column>
                    <Grid.Column width={3}>
                      <Field
                          name="resumption2"
                          component={Input}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={7}>
                      {getMessage("doctorReport.modal.startingWork.allocateAppropriateWork")}
                    </Grid.Column>
                    <Grid.Column width={2}>
                      <Field
                          name="allocateAppropriateWork"
                          component={Select}
                          options={yesNoOptions}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={6}>
                      {getMessage("doctorReport.modal.startingWork.permanentDisadvantage")}
                    </Grid.Column>
                    <Grid.Column width={2}>
                      <Field
                          name="permanentDisadvantage"
                          component={Select}
                          options={yesNoOptions}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={3}>
                      {getMessage("doctorReport.modal.whenYes")}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={14}>
                      <Field
                          name="permanentDisadvantageTrue"
                          component={TextArea}
                          fluid
                          disabled={values.permanentDisadvantage === "No"}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <StyledBorderGridRow/>
                  <Grid.Row>
                    <Grid.Column width={2} style={{fontWeight: "bold"}}>
                      {getMessage("doctorReport.modal.remarks")}
                    </Grid.Column>
                    <Grid.Column width={14}>
                      <Field
                          name="remarks"
                          component={TextArea}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <StyledBorderGridRow/>
                  <Grid.Row>
                    <Grid.Column width={2} style={{fontWeight: "bold"}}>
                      {getMessage("doctorReport.modal.additionalField")}
                    </Grid.Column>
                    <Grid.Column width={14}>
                      <Field
                          name="additionalField"
                          component={TextArea}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                </PraxFormGrid>
              </FormContent>

              <FormButtonsRow>
                <Button compact
                        primary
                        disabled={submitting}
                >
                  {getMessage("synopsisReport.modal.generateReport.button")}
                </Button>
                <Button type="button"
                        compact
                        disabled={submitting}
                        onClick={() => onCloseModal(false)}>
                  {getMessage("button.cancel")}
                </Button>
              </FormButtonsRow>
            </FormContainer>

            {
                doctorReportPdfModalOpen
                && <PatientReportPdfModal
                    pdfResponses={[doctorReportPdfResponse]}
                    modalOpen={doctorReportPdfModalOpen}
                    onCloseModal={closeDoctorReportPdf}
                    getPdfUrl={getDoctorReportPdfUrl}
                    onSave={saveDocument}
                    onSendEmail={sendEmail}
                    sendEmailTextKey={getMessage("doctorReport.modal.pdfModal.email.send")}
                    doctorEmail={email}
                    patient={patient}
                    reportDto={reportDto}
                    pdfDocumentDescription={"Doctor Report"}
                    documentType={PatientDocumentType.DOCTOR_REPORT}
                />
            }
          </form>
        </React.Fragment>
    )
  }

  return (
      <Modal
          open={modalOpen}
          onClose={() => onCloseModal(false)}
          closeOnDimmerClick={false}
      >
        <Modal.Header>
          <ModalHeaderDiv>
            {getMessage("doctorReport.modal.header")}
            {renderModalHeader()}
          </ModalHeaderDiv>
        </Modal.Header>
        <FormModalContentContainer>
          {!requiredDataLoaded
              ? <LoaderComponent message={getMessage("loading")}/>
              : <FinalForm
                  onSubmit={(values) => openDoctorReport(values)}
                  subscription={{submitting: true, pristine: true, values: true}}
                  initialValues={initialValues.current}
                  mutators={{
                    ...arrayMutators
                  }}
                  render={renderForm}
              />
          }
        </FormModalContentContainer>
      </Modal>
  );

};

export default withMessages(withAuthContext(UpsertDoctorReportModal));
